import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { dataLibraryApi, fetchAllPages, MAX_GROUPED_ROWS } from '../../../services/api/dataLibrary';
import type { DataLibraryParticipantParams } from '../../../services/api/dataLibrary';
import type { DataLibraryParticipant, DataLibraryFilterOptions, ColumnAverages } from '../../../types';
import type { SortConfig } from '../../../hooks/useColumnConfig';
import type { ColumnConfig } from '../../../hooks/useColumnConfig';
import type { ActiveFilterRow } from '../../common/FilterPanel/FilterPanel';
import type { SortState } from '../../../utils/sortTasks';
import type { GroupState } from '../../common/GroupPanel/GroupPanel';
import { groupDataByLevels, normalizeGroupState, type GroupNode } from '../../../utils/groupDataByField';
import { DATA_LIBRARY_COLUMNS } from './dataLibraryColumns';
import {
  labelGroupCoachingColumns,
  type GroupCoachingPrefix,
} from '../../../utils/attendanceModalityLabels';
import { useDataLibraryColumns } from '../useDataLibraryTableLayout';

export interface ParticipantsTabConfig {
  quickFilters?: string[];
  sort?: SortConfig;
  groupBy?: GroupState | null;
  visibleColumnIds?: string[];
}

interface UseDataLibraryParticipantsProps {
  selectedCohortIds: string[];
  initialConfig?: ParticipantsTabConfig;
}

export function useDataLibraryParticipants({ selectedCohortIds, initialConfig }: UseDataLibraryParticipantsProps) {
  // Data state
  const [participants, setParticipants] = useState<DataLibraryParticipant[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  // MED-1442: kept from the last response, so the label holds while a page loads
  const [groupCoachingPrefix, setGroupCoachingPrefix] = useState<GroupCoachingPrefix>();
  // MDF-41: server averages over all matching rows, by column id
  const [averages, setAverages] = useState<ColumnAverages>({});
  const [totalPages, setTotalPages] = useState(0);
  const [filterOptions, setFilterOptions] = useState<DataLibraryFilterOptions>({
    groups: [],
    coaches: [],
    departments: [],
    locations: [],
  });

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Search
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);

  // Sort (server-side)
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(
    initialConfig?.sort ?? { key: 'first_name', order: 'ASC' },
  );

  // Filters
  const [activeQuickFilters, setActiveQuickFilters] = useState<string[]>(
    initialConfig?.quickFilters ?? [],
  );
  const [activeFilters, setActiveFilters] = useState<ActiveFilterRow[]>([]);

  // Grouping (client-side)
  const [activeGroup, setActiveGroup] = useState<GroupState | null>(() =>
    normalizeGroupState(initialConfig?.groupBy),
  );

  // Column config — apply visibleColumnIds override if provided
  const [defaultColumns] = useState<ColumnConfig[]>(() => {
    if (!initialConfig?.visibleColumnIds) return DATA_LIBRARY_COLUMNS;
    return DATA_LIBRARY_COLUMNS.map(col => ({
      ...col,
      visible: initialConfig.visibleColumnIds!.includes(col.id),
    }));
  });
  // MDF-209: a drill-down preset neither reads nor overwrites the saved layout.
  const layoutColumns = useDataLibraryColumns(
    defaultColumns,
    !initialConfig?.visibleColumnIds,
  );

  // MED-1442: "PG" for peer led cohorts. Labels only; the layout keys on id.
  const { columns: storedColumns, setColumns } = layoutColumns;
  const columns = useMemo(
    () => labelGroupCoachingColumns(storedColumns, groupCoachingPrefix),
    [storedColumns, groupCoachingPrefix],
  );
  const visibleColumns = useMemo(() => columns.filter(c => c.visible), [columns]);

  // Build API params from state
  const buildParams = useCallback((): DataLibraryParticipantParams => {
    const params: DataLibraryParticipantParams = {
      cohortIds: selectedCohortIds,
      page,
      limit: pageSize,
      ...(sortConfig && { sortBy: sortConfig.key, sortOrder: sortConfig.order }),
    };

    if (debouncedSearch) params.search = debouncedSearch;

    // Quick filters
    if (activeQuickFilters.includes('flagged')) params.flaggedOnly = true;
    if (activeQuickFilters.includes('absences')) params.hasAbsences = true;

    // Active filters
    for (const filter of activeFilters) {
      if (filter.values.length === 0) continue;
      switch (filter.key) {
        case 'status':
          params.status = filter.values[0];
          break;
        case 'group':
          params.groupId = filter.values.join(',');
          break;
        case 'coach':
          params.coachId = filter.values[0];
          break;
        case 'department':
          params.department = filter.values[0];
          break;
        case 'location':
          params.location = filter.values[0];
          break;
        case 'midpoint':
          params.midpointCompleted = filter.values[0] === 'true';
          break;
        case 'endpoint':
          params.endpointCompleted = filter.values[0] === 'true';
          break;
      }
    }

    return params;
  }, [
    selectedCohortIds,
    page,
    pageSize,
    sortConfig,
    debouncedSearch,
    activeQuickFilters,
    activeFilters,
  ]);

  // MDF-39: the filtered total when it is over MAX_GROUPED_ROWS; the table then stays paged
  const [overGroupLimit, setOverGroupLimit] = useState<number | null>(null);
  const isGrouped = !!activeGroup && overGroupLimit === null;
  // MDF-39: only the newest request may write state; a grouped load spans many requests
  const latestRequest = useRef(0);

  // Fetch data
  const fetchParticipants = useCallback(async () => {
    const requestId = ++latestRequest.current;
    if (selectedCohortIds.length === 0) {
      setParticipants([]);
      setTotalCount(0);
      setAverages({});
      setTotalPages(0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const params = buildParams();
      // MDF-39: a grouped table needs every row, so it fetches all pages
      const result = isGrouped
        ? await fetchAllPages((page, limit) => dataLibraryApi.getParticipants({ ...params, page, limit }), () => requestId !== latestRequest.current)
        : await dataLibraryApi.getParticipants(params);
      if (requestId !== latestRequest.current) return;
      if (result.meta.group_coaching_prefix) setGroupCoachingPrefix(result.meta.group_coaching_prefix);
      const total = result.meta.pagination.total;
      setOverGroupLimit(total > MAX_GROUPED_ROWS ? total : null);
      if (isGrouped && total > MAX_GROUPED_ROWS) return;

      setParticipants(result.data || []);
      setTotalCount(result.meta.pagination.total);
      setAverages(result.meta.averages);
      setTotalPages(result.meta.pagination.totalPages);
      setFilterOptions(result.meta.filterOptions);
    } catch {
      if (requestId !== latestRequest.current) return;
      notifications.show({
        title: 'Error',
        message: 'Failed to load participants',
        color: 'red',
      });
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [selectedCohortIds, buildParams, isGrouped]);

  // Fetch on dependency change
  useEffect(() => {
    fetchParticipants();
  }, [fetchParticipants]);

  // Reset page on filter/search change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, activeQuickFilters, activeFilters, selectedCohortIds]);

  // Sort handler (for both SortPanel and table header clicks)
  const handleSort = useCallback((key: string) => {
    setSortConfig(prev => ({
      key,
      order: prev?.key === key && prev.order === 'ASC' ? 'DESC' : 'ASC',
    }));
  }, []);

  const handleSortPanelChange = useCallback((sort: SortState | null) => {
    if (sort) {
      setSortConfig({ key: sort.field, order: sort.direction === 'asc' ? 'ASC' : 'DESC' });
    } else {
      setSortConfig(null);
    }
  }, []);

  // Quick filter toggle
  const handleQuickFilterToggle = useCallback((key: string) => {
    setActiveQuickFilters(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key],
    );
  }, []);

  // Filter handlers
  const handleFilterAdd = useCallback((key: string) => {
    setActiveFilters(prev => {
      if (prev.some(f => f.key === key)) return prev;
      return [...prev, { key, values: [] }];
    });
  }, []);

  const handleFilterChange = useCallback((key: string, values: string[]) => {
    setActiveFilters(prev => prev.map(f => (f.key === key ? { ...f, values } : f)));
  }, []);

  const handleFilterRemove = useCallback((key: string) => {
    setActiveFilters(prev => prev.filter(f => f.key !== key));
  }, []);

  const clearAllFilters = useCallback(() => {
    setActiveQuickFilters([]);
    setActiveFilters([]);
    setSearchValue('');
  }, []);

  const hasActiveFilters =
    activeQuickFilters.length > 0 ||
    activeFilters.some(f => f.values.length > 0) ||
    debouncedSearch.length > 0;

  // Grouped data (client-side grouping)
  const groupedParticipants = useMemo(() => {
    return groupDataByLevels(
      participants as unknown as Record<string, unknown>[],
      isGrouped ? activeGroup : null,
      columns,
    ) as GroupNode<DataLibraryParticipant>[] | null;
  }, [participants, activeGroup, isGrouped, columns]);

  return {
    groupLimitTotal: activeGroup ? overGroupLimit : null,
    // Data
    participants,
    groupedParticipants,
    loading,
    totalCount,
    averages,
    totalPages,
    filterOptions,

    // Pagination
    page,
    setPage,
    pageSize,
    setPageSize: (size: number) => {
      setPageSize(size);
      setPage(1);
    },

    // Search
    searchValue,
    setSearchValue,

    // Sort
    sortConfig,
    handleSort,
    handleSortPanelChange,

    // Filters
    activeQuickFilters,
    handleQuickFilterToggle,
    activeFilters,
    handleFilterAdd,
    handleFilterChange,
    handleFilterRemove,
    clearAllFilters,
    hasActiveFilters,

    // Grouping
    activeGroup,
    setActiveGroup,

    // Columns
    columns,
    setColumns,
    visibleColumns,

    // Actions
    fetchParticipants,
  };
}
